import { BookmarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import Switch from 'antd/es/switch';
import type { Dispatch } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useOpenNotification } from 'utils/antd/notification-toasts';
import {
  formatDecimalAmount,
  formatRenderedCurrency
} from 'utils/formatters/formatCurrency';
import { dispatchGAEvent } from 'utils/googleAnalytics';
import { hasValue } from 'utils/hasValue';
import { logEventToHotjar } from 'utils/logging';

import { useVirtualAccounts } from 'hooks/context';
import { useFeatureAccessControl } from 'hooks/useFeatureAccessControl';

import { PrimaryButton } from '@atoms/buttons/PrimaryButton';

import { useDraftDefinition } from './contexts/DraftDefinitionProvider';
import { useDraftProgress } from './contexts/DraftProgressProvider';
import { FakeTopBar } from './FakeTopBar';
import { useDraftStepProgress } from './hooks/useDraftStepProgress';
import { usePublishDraft } from './hooks/usePublishDraft';
import { useSaveReceiver } from './hooks/useSaveReceiver';
import { PayoutDraftPreview } from './PayoutDraftPreview';
import type { SubmittedPayout } from './utils/types';

type CreateManualPayoutFlowProps = {
  onSubmitted: Dispatch<SubmittedPayout | undefined>;
};

export const ReviewDraft = ({ onSubmitted }: CreateManualPayoutFlowProps) => {
  const { data, selectedReceiver } = useDraftProgress();
  const { previous } = useDraftStepProgress();
  const { product } = useDraftDefinition();
  const publishDraft = usePublishDraft();

  const { findAccountByAccountNumber } = useVirtualAccounts();

  const { isReceiverManagementEnabled } = useFeatureAccessControl();

  const notification = useOpenNotification();

  const saveReceiver = useSaveReceiver();

  const [shouldSaveReceiver, setShouldSaveReceiver] = useState(false);

  const formattedAmount = useMemo(() => {
    const amount = data.payout_request?.amount as string;
    const accountNumber = data.payout_request?.virtual_account_number as string;
    const account = findAccountByAccountNumber(accountNumber);
    const currencyCode = account?.currency_code;
    return hasValue(currencyCode)
      ? formatRenderedCurrency(Number(amount), String(currencyCode))
      : formatDecimalAmount(Number(amount));
  }, [data, findAccountByAccountNumber]);

  const publishReviewedDraft = useCallback(async () => {
    const response = await publishDraft();

    if (response.data === undefined) {
      return onSubmitted(undefined);
    }

    if (isReceiverManagementEnabled && shouldSaveReceiver) {
      saveReceiver();
    }

    // Case where payout is sent to review
    if (response.data.state === 'waiting_review') {
      onSubmitted({
        state: 'waiting_review',
        id: (response.data as { id: number }).id
      });
    }

    // Case where payout is submitted directly
    if (response.data.unique_reference) {
      onSubmitted({
        state: 'received',
        uniqueReference: response.data.unique_reference
      });
    }

    logEventToHotjar('new-manual-payout:submitted');

    dispatchGAEvent('create_manual_payout_submitted', {
      version: 'v2'
    });
  }, [
    publishDraft,
    isReceiverManagementEnabled,
    shouldSaveReceiver,
    onSubmitted,
    saveReceiver
  ]);

  const onPublishDraft = useCallback(() => {
    publishReviewedDraft().catch(() => {
      notification({
        kind: 'error',
        message: 'Publishing payout failed',
        description:
          'An unexpected error occurred. Please ensure the data is correct and try again.'
      });
    });
  }, [notification, publishReviewedDraft]);

  return (
    <div className="size-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-md">
      <FakeTopBar height={12} />
      <div className="space-y-4 pt-2">
        <h1
          className="px-6 text-lg font-semibold"
          data-testid="manual-payout-summary"
        >
          Summary
        </h1>
        <div className="mx-6 flex h-36 flex-col items-center justify-center gap-2 rounded-2xl bg-gray-50">
          <span className="text-sm font-medium text-content-secondary">
            Total amount
          </span>
          <span className="text-2xl font-semibold text-content-positive-soft">
            {formattedAmount}
          </span>
          <span className="flex items-center gap-1.5 rounded-2xl bg-surface-selected px-3 py-1.5 text-sm font-medium text-content-positive-soft">
            <CheckCircleIcon className="size-5" />
            {product.local_instrument} Transfer
          </span>
        </div>
        <PayoutDraftPreview />
      </div>
      {isReceiverManagementEnabled && selectedReceiver === undefined && (
        <div className="m-6 mt-0 flex items-center justify-between rounded-md border p-4">
          <div className="flex gap-4">
            <div className="flex size-9 items-center justify-center rounded-md bg-surface-muted">
              <BookmarkIcon className="size-6" />
            </div>
            <div className="flex flex-col justify-between">
              <h4 className="text-sm font-medium text-content-primary">
                Save receiver details for future use
              </h4>
              <p className="text-xs text-content-strong">
                Create future payouts quicker
              </p>
            </div>
          </div>
          <Switch
            value={shouldSaveReceiver}
            onChange={(checked) => setShouldSaveReceiver(checked)}
          />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 p-6 pt-4">
        <PrimaryButton
          className="h-9 border-2 border-content-brand-soft font-medium"
          onClick={previous}
        >
          Previous
        </PrimaryButton>
        <PrimaryButton onClick={onPublishDraft} className="h-9 font-medium">
          Confirm
        </PrimaryButton>
      </div>
    </div>
  );
};
